import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { errText, clock, fmt } from "@/lib/api";
import LikertSection from "@/candidate/LikertSection";
import TextSection from "@/candidate/TextSection";
import FrtSection from "@/candidate/FrtSection";
import { TRI_LOGO, ORG_NAME, APP_NAME } from "@/lib/brand";
import ProctorCam from "@/candidate/ProctorCam";
import useIntegrityMonitor from "@/candidate/useIntegrityMonitor";

export default function CandidateApp() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("loading"); // loading | consent | running | done
  const [answers, setAnswers] = useState({});
  const [sectionIdx, setSectionIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const integrity = useIntegrityMonitor(token, stage === "running");
  const integrityRef = useRef(integrity);
  integrityRef.current = integrity;
  const [agreed, setAgreed] = useState(false);
  const [fullName, setFullName] = useState("");
  const [doneInfo, setDoneInfo] = useState(null);
  const [camStatus, setCamStatus] = useState("idle");
  const dirty = useRef(false);
  const answersRef = useRef({});
  const sectionRef = useRef(0);

  answersRef.current = answers;
  sectionRef.current = sectionIdx;

  const loadState = useCallback(async () => {
    const { data } = await api.get(`/assess/${token}/state`);
    setAnswers(data.answers || {});
    setSectionIdx(data.current_section || 0);
    setRemaining(data.remaining_seconds || 0);
    setSavedAt(data.last_saved_at);
    if (data.submitted) setStage("done");
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/assess/${token}`);
        setMeta(data);
        setFullName(data.candidate_name || "");
        if (data.submitted) {
          setDoneInfo({ submitted_at: data.submitted_at, reference: data.reference });
          setStage("done");
        } else if (data.consent_given) {
          await loadState();
          setStage((s) => (s === "done" ? "done" : "running"));
        } else {
          setRemaining(data.remaining_seconds);
          setStage("consent");
        }
      } catch (e) {
        setError(errText(e));
        setStage("error");
      }
    })();
  }, [token, loadState]);

  // server-authoritative countdown, ticked locally and resynced on every save
  useEffect(() => {
    if (stage !== "running") return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [stage]);

  const save = useCallback(async () => {
    if (!dirty.current) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/assess/${token}/autosave`, {
        answers: answersRef.current,
        current_section: sectionRef.current,
      });
      dirty.current = false;
      setSavedAt(data.saved_at);
      setRemaining(data.remaining_seconds);
    } catch (e) {
      setError(errText(e));
    } finally {
      setSaving(false);
    }
  }, [token]);

  useEffect(() => {
    if (stage !== "running") return;
    const t = setInterval(save, 10000);
    return () => clearInterval(t);
  }, [stage, save]);

  const submit = useCallback(
    async (auto = false) => {
      setSubmitting(true);
      try {
        await integrityRef.current.flush();
        const { data } = await api.post(`/assess/${token}/submit`, {
          answers: answersRef.current,
          current_section: sectionRef.current,
        });
        setDoneInfo({ ...data, auto_submitted: auto || data.auto_submitted });
        setStage("done");
      } catch (e) {
        setError(errText(e));
        await loadState();
      } finally {
        setSubmitting(false);
      }
    },
    [token, loadState],
  );

  useEffect(() => {
    if (stage === "running" && remaining === 0 && !submitting) submit(true);
  }, [stage, remaining, submitting, submit]);

  const setSectionAnswers = (key, value) => {
    dirty.current = true;
    setAnswers((a) => ({ ...a, [key]: value }));
  };

  if (stage === "loading")
    return (
      <Centered>
        <span
          data-testid="candidate-loading"
          className="flex items-center gap-2.5 text-sm text-[color:var(--cand-muted)]"
        >
          <span className="inline-block w-4 h-4 rounded-full border-2 border-[color:var(--cand-border)] border-t-[color:var(--cand-accent)] animate-spin" />
          Checking your link…
        </span>
      </Centered>
    );

  if (stage === "error")
    return (
      <Centered>
        <div className="cand-card rounded-lg p-8 max-w-md" data-testid="candidate-link-error">
          <h1 className="font-serif-display text-2xl mb-3">This link cannot be opened</h1>
          <p className="text-sm text-[color:var(--cand-muted)]">{error}</p>
          <p className="text-sm text-[color:var(--cand-muted)] mt-4">
            If you believe this is a mistake, reply to the email you received and ask for a new
            link.
          </p>
        </div>
      </Centered>
    );

  if (stage === "done")
    return (
      <Centered>
        <div className="cand-card rounded-lg p-8 sm:p-10 max-w-xl w-full" data-testid="candidate-submitted">
          <img src={TRI_LOGO} alt={ORG_NAME} style={{ height: 34, width: "auto" }} className="mb-6" />
          <h1 className="font-serif-display text-3xl sm:text-4xl mb-4">Submitted. Thank you.</h1>
          <p className="text-sm sm:text-base text-[color:var(--cand-muted)] leading-relaxed">
            Your answers have been received and this link is now closed. The hiring team reviews
            every assessment by hand before making any decision, so results are not shown here.
          </p>
          {doneInfo?.auto_submitted && (
            <p className="text-sm mt-4 text-[color:var(--cand-accent)]" data-testid="auto-submit-note">
              The time limit was reached, so your saved work was submitted automatically.
            </p>
          )}
          <dl className="mt-8 text-sm font-data border-t pt-5 space-y-2">
            <div className="flex justify-between">
              <dt className="text-[color:var(--cand-muted)]">Reference</dt>
              <dd data-testid="submission-reference">{doneInfo?.reference || meta?.reference}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[color:var(--cand-muted)]">Submitted at</dt>
              <dd data-testid="submission-timestamp">
                {fmt(doneInfo?.submitted_at || meta?.submitted_at)}
              </dd>
            </div>
          </dl>
          <p className="text-xs text-[color:var(--cand-muted)] mt-6">
            You may close this window. Keep the reference above if you need to contact the hiring
            team.
          </p>
        </div>
      </Centered>
    );

  if (stage === "consent")
    return (
      <div className="cand-shell min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
          <div className="flex items-center gap-3 mb-8">
            <img src={TRI_LOGO} alt={ORG_NAME} style={{ height: 34, width: "auto" }} data-testid="tri-logo-candidate" />
            <span className="text-xs tracking-[0.18em] uppercase text-[color:var(--cand-muted)] border-l pl-3">
              {APP_NAME}
            </span>
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {meta.battery.name}
          </h1>
          <p className="mt-4 text-sm sm:text-base text-[color:var(--cand-muted)] max-w-2xl leading-relaxed">
            {meta.battery.description}
          </p>

          <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px cand-card rounded-lg overflow-hidden">
            <Fact label="Candidate" value={meta.candidate_name} testId="consent-candidate-name" />
            <Fact label="Role applied" value={meta.role_applied} testId="consent-role" />
            <Fact label="Time limit" value={`${meta.battery.time_limit_minutes} min`} testId="consent-time-limit" />
            <Fact label="Sections" value={String(meta.battery.sections.length)} testId="consent-section-count" />
          </dl>

          <div className="cand-card rounded-lg p-6 sm:p-8 mt-8">
            <h2 className="text-lg md:text-lg font-semibold mb-4">Consent</h2>
            <div
              className="text-sm text-[color:var(--cand-muted)] whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto pr-2"
              data-testid="consent-text"
            >
              {meta.consent_text}
            </div>

            {meta.proctoring?.enabled && (
              <div
                className="mt-5 rounded-lg p-4 border-l-4"
                style={{ background: "#FBEFEC", borderColor: "var(--cand-accent)" }}
                data-testid="proctoring-disclaimer"
              >
                <p className="text-xs font-data uppercase tracking-wider text-[color:var(--cand-accent)] mb-2">
                  Camera proctoring — please read
                </p>
                <div className="text-sm text-[color:var(--cand-muted)] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto pr-2">
                  {meta.proctoring_text}
                </div>
                <div className="mt-4">
                  <ProctorCam
                    token={token}
                    active={false}
                    snapshots={meta.proctoring.snapshots}
                    onStatus={setCamStatus}
                  />
                  <p className="text-xs font-data" data-testid="consent-camera-status">
                    Camera check:{" "}
                    <span style={{ color: camStatus === "live" ? "#047857" : "#B91C1C" }}>
                      {camStatus === "live" ? "working" : camStatus}
                    </span>
                  </p>
                  {camStatus !== "live" && (
                    <p className="text-xs text-[color:var(--cand-muted)] mt-1">
                      You cannot begin until the camera is working. Allow camera access when your
                      browser asks.
                    </p>
                  )}
                </div>
              </div>
            )}
            <label className="mt-6 flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 accent-[#C05621]"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="candidate-consent-checkbox"
              />
              <span className="text-sm">
                I have read and agree to the above{meta.proctoring?.enabled ? ", including the camera proctoring and photographs" : ""}. I am{" "}
                {meta.candidate_name}, and my answers will be my own work.
              </span>
            </label>
            <div className="mt-5">
              <label className="block text-xs uppercase tracking-wider text-[color:var(--cand-muted)] mb-2">
                Type your full name to sign
              </label>
              <input
                className="w-full sm:w-80 border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#EA580C]"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                data-testid="consent-signature-input"
              />
            </div>
            {error && (
              <p className="text-sm text-red-700 mt-4" data-testid="consent-error">
                {error}
              </p>
            )}
            <button
              className="btn-accent rounded px-6 py-3 text-sm font-medium mt-6"
              disabled={
                !agreed ||
                fullName.trim().length < 2 ||
                (meta.proctoring?.enabled && meta.proctoring?.required && camStatus !== "live")
              }
              data-testid="start-assessment-button"
              onClick={async () => {
                setError("");
                try {
                  await api.post(`/assess/${token}/consent`, {
                    agreed: true,
                    full_name: fullName,
                    camera_confirmed: camStatus === "live",
                  });
                  await loadState();
                  setStage("running");
                } catch (e) {
                  setError(errText(e));
                }
              }}
            >
              Begin assessment
            </button>
            <p className="text-xs text-[color:var(--cand-muted)] mt-3">
              The clock starts when you press this and keeps running on our server, even if you
              close the page.
            </p>
          </div>
        </div>
      </div>
    );

  const section = meta.battery.sections[sectionIdx];
  const last = sectionIdx === meta.battery.sections.length - 1;
  const sectionAnswers = answers[section.key] || (section.type === "likert" ? {} : {});

  return (
    <div className="cand-shell min-h-screen">
      {integrity.awayNotice && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-md w-[92%] rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg"
          data-testid="integrity-away-notice"
        >
          <p className="text-sm text-amber-900">
            You left this assessment for {integrity.awayNotice.seconds}s. This has been recorded
            and will be shown to the reviewer along with your answers.
          </p>
          <button
            className="mt-2 text-xs underline text-amber-900"
            data-testid="integrity-away-dismiss"
            onClick={integrity.dismissAwayNotice}
          >
            I understand
          </button>
        </div>
      )}
      {meta.proctoring?.enabled && (
        <ProctorCam
          token={token}
          active
          snapshots={meta.proctoring.snapshots}
          onStatus={setCamStatus}
        />
      )}
      <div
        className="sticky top-0 z-20 border-b backdrop-blur"
        style={{ background: "rgba(251,249,246,0.92)" }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs sm:text-sm font-data" data-testid="section-indicator">
            Section {sectionIdx + 1} of {meta.battery.sections.length}
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <span
              className="text-xs text-[color:var(--cand-muted)] hidden sm:inline"
              data-testid="autosave-status-indicator"
            >
              {saving ? "Saving…" : savedAt ? `Draft saved ${fmt(savedAt).split(", ")[1] || ""}` : "Not saved yet"}
            </span>
            <span
              className={`font-data text-sm sm:text-base px-2 py-1 rounded ${
                remaining < 300 ? "bg-red-100 text-red-800" : "bg-white border"
              }`}
              data-testid="server-timer-display"
            >
              {clock(remaining)}
            </span>
          </div>
        </div>
        <div className="h-0.5 bg-[#E7E5E4]">
          <div
            className="h-0.5 transition-all duration-300"
            style={{
              background: "var(--cand-accent)",
              width: `${((sectionIdx + 1) / meta.battery.sections.length) * 100}%`,
            }}
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <h1 className="font-serif-display text-3xl sm:text-4xl leading-tight" data-testid="section-title">
          {section.title}
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[color:var(--cand-muted)] leading-relaxed">
          {section.instructions}
        </p>

        <div className="mt-8">
          {section.type === "likert" && (
            <LikertSection
              section={section}
              value={sectionAnswers}
              onChange={(v) => setSectionAnswers(section.key, v)}
            />
          )}
          {section.type === "free_text" && (
            <TextSection
              section={section}
              value={sectionAnswers}
              onChange={(v) => setSectionAnswers(section.key, v)}
              integrity={integrity}
            />
          )}
          {section.type === "two_phase" && (
            <FrtSection
              section={section}
              value={sectionAnswers}
              onChange={(v) => setSectionAnswers(section.key, v)}
              onLockPhase1={save}
              integrity={integrity}
            />
          )}
        </div>

        {error && (
          <p className="text-sm text-red-700 mt-6" data-testid="assessment-error">
            {error}
          </p>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t pt-6">
          <button
            className="px-5 py-2.5 text-sm border rounded bg-white hover:bg-[#F5F5F4] disabled:opacity-40"
            disabled={sectionIdx === 0}
            data-testid="previous-section-button"
            onClick={async () => {
              await save();
              setSectionIdx((i) => Math.max(0, i - 1));
            }}
          >
            Back
          </button>
          <button
            className="px-5 py-2.5 text-sm border rounded bg-white hover:bg-[#F5F5F4]"
            data-testid="save-draft-button"
            onClick={() => {
              dirty.current = true;
              save();
            }}
          >
            Save draft
          </button>
          {!last ? (
            <button
              className="btn-accent rounded px-6 py-2.5 text-sm font-medium"
              data-testid="submit-section-button"
              onClick={async () => {
                dirty.current = true;
                await save();
                setSectionIdx((i) => i + 1);
                window.scrollTo({ top: 0 });
              }}
            >
              Save and continue
            </button>
          ) : (
            <button
              className="btn-accent rounded px-6 py-2.5 text-sm font-medium"
              disabled={submitting}
              data-testid="final-submit-button"
              onClick={async () => {
                dirty.current = true;
                await save();
                submit(false);
              }}
            >
              {submitting ? "Submitting…" : "Submit assessment"}
            </button>
          )}
        </div>
        {last && (
          <p className="text-xs text-[color:var(--cand-muted)] mt-3">
            Once submitted you cannot return to this assessment.
          </p>
        )}
      </div>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="cand-shell min-h-screen flex items-center justify-center px-4 py-12">{children}</div>
);

const Fact = ({ label, value, testId }) => (
  <div className="bg-white p-4">
    <div className="text-[10px] uppercase tracking-widest text-[color:var(--cand-muted)]">{label}</div>
    <div className="text-sm mt-1 font-medium" data-testid={testId}>
      {value}
    </div>
  </div>
);
